import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import { Link, useSearchParams } from 'react-router-dom'

import { OutlineButton } from '@/app/components/OutlineButton'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline3, Headline4 } from '@/app/components/Typography'
import { DashboardIcon } from '@/app/icons/DashboardIcon'
import { MydraDuoLogo } from '@/app/icons/MydraDuoLogo/MydraDuoLogo'
import { ROUTES } from '@/app/lib/constants'
import { useAuth } from '@/app/providers/Auth/Auth'
import * as CookieConsent from 'vanilla-cookieconsent'

export function MePage() {
  const intl = useIntl()

  const { firstName, lastName, email } = useAuth()
  const [searchParams] = useSearchParams()

  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()

  const marketplaceUrl = import.meta.env.VITE_PUBLIC_MARKETPLACE_HOST_URL!
  const dashboardUrl = import.meta.env.VITE_PUBLIC_PARTNER_DASHBOARD_URL!

  const continueUrl = searchParams.get('redirectTo') ?? marketplaceUrl

  return (
    <>
      <Helmet>
        <style>
          {` .mydra-base-layout { min-height: 0; height: 100vh; } `}
        </style>
      </Helmet>
      <div className="relative flex flex-col h-full md:flex-row">
        <Link
          to={ROUTES.homepage}
          className="z-10 text-black md:text-white md:absolute"
        >
          <MydraDuoLogo className="h-8 mx-auto mt-12 md:mt-7 md:ml-9" />
        </Link>
        <div className="relative flex-col items-center justify-center hidden gap-8 p-8 text-center md:w-1/2 md:flex">
          <div className="absolute w-full h-full p-2">
            <video
              className="object-cover w-full h-full rounded-xl filter brightness-[75%]"
              src="https://assets.mydra.io/videos/login-page-video.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          </div>
          <Headline4 className="z-10 max-w-prose !text-4xl font-title text-white">
            {intl.formatMessage({
              id: 'Me.make-your-own-path',
              defaultMessage: 'Make your own path',
            })}
          </Headline4>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 w-full mt-6 overflow-y-auto md:mt-0 md:w-1/2 md:bg-cover">
          <Headline3 className="font-sans mb-6">
            {intl.formatMessage({
              id: 'Me.already-logged-in',
              defaultMessage: 'Already logged in',
            })}
          </Headline3>

          <div className="w-[320px]">
            <div className="flex items-center p-4 bg-gray-50 rounded-lg shadow-sm max-w-md">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-purple-500 flex items-center justify-center text-white text-base font-semibold">
                    {initials}
                  </div>
                </div>
                <div className="flex flex-col">
                  <h2 className="text-base font-semibold text-gray-800">
                    {`${firstName} ${lastName}`}
                  </h2>
                  <p className="text-sm text-gray-600">{email}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:gap-4 mt-4">
              <PrimaryButton className="w-full" href={continueUrl}>
                {intl.formatMessage({ id: 'Me.continue-as' }, { firstName })}
              </PrimaryButton>
              <p className="mt-1 text-xs md:text-sm text-center">
                <Link
                  to={ROUTES.logout}
                  className="font-semibold text-center text-mydra-black"
                >
                  {intl.formatMessage({
                    id: 'Me.logout',
                    defaultMessage: 'Logout',
                  })}
                </Link>
              </p>
              <hr
                className="my-5 text-xs uppercase md:mt-2 border-lines hr-text"
                data-content={intl.formatMessage({
                  id: 'Me.or',
                  defaultMessage: 'or',
                })}
              />
              <OutlineButton
                className="w-full !text-sm font-semibold"
                onClick={(e) => {
                  e.preventDefault()
                  window.location.href = dashboardUrl
                }}
              >
                <div className="flex items-center w-full">
                  <div className="flex-none w-6 ml-2 mr-4 flex justify-center">
                    <DashboardIcon />
                  </div>
                  <div className="flex-1 text-center">
                    {intl.formatMessage({
                      id: 'Me.go-to-dashboard',
                      defaultMessage: 'Go to Dashboard',
                    })}
                  </div>
                  <div className="flex-none w-6 mr-2"></div>{' '}
                </div>
              </OutlineButton>
              <button
                type="button"
                className="absolute bottom-0 right-0 p-4 text-sm "
                onClick={() => {
                  CookieConsent.showPreferences()
                }}
              >
                Cookie preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
